/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  ApolloClient,
  ApolloLink,
  HttpLink as ApolloHttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { getAuth } from "firebase/auth";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import FirebaseApp from "@/utils/firebase";
import { getMainDefinition } from "@apollo/client/utilities";
import { getImpersonationToken } from "@/utils/impersonation";
import {
  DEFAULT_RETRY_CONFIG,
  calculateRetryDelay,
} from "@/types/errors";

const AuthLink = setContext(async (_, ctx) => {
  const { headers } = ctx;
  try {
    // Check for impersonation token first (admin feature)
    const impersonationToken = getImpersonationToken();
    if (impersonationToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${impersonationToken}`,
        },
      };
    }

    // Otherwise, use Firebase token
    const firebaseAuth = getAuth(FirebaseApp);
    const token = await firebaseAuth.currentUser?.getIdToken();

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  } catch {
    return {
      headers,
    };
  }
});

/**
 * Error Link for handling GraphQL and network errors
 * Logs errors, handles auth errors, and integrates with error context
 */
const ErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Handle GraphQL errors
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const logMessage = `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`;

      if (process.env.NODE_ENV === "development") {
        console.error(logMessage);
        console.error("Extensions:", extensions);
      }

      // Handle authentication errors
      if (
        extensions?.code === "UNAUTHENTICATED" ||
        extensions?.code === "FORBIDDEN"
      ) {
        // Redirect to login on auth errors
        if (typeof window !== "undefined") {
          // Clear Firebase auth
          const auth = getAuth(FirebaseApp);
          auth.signOut().then(() => {
            window.location.href = "/prihlasenie";
          });
        }
      }
    });
  }

  // Handle network errors
  if (networkError) {
    const logMessage = `[Network error]: ${networkError.message}`;

    if (process.env.NODE_ENV === "development") {
      console.error(logMessage);
      console.error("Network error details:", networkError);
    }

    // Handle specific network error cases
    const networkErr = networkError as Error & { statusCode?: number };

    // Auth errors (401)
    if (networkErr.statusCode === 401) {
      if (typeof window !== "undefined") {
        const auth = getAuth(FirebaseApp);
        auth.signOut().then(() => {
          window.location.href = "/prihlasenie";
        });
      }
    }

    // Log to Sentry in production
    if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
      // @ts-ignore
      if (window.Sentry) {
        // @ts-ignore
        window.Sentry.captureException(networkError, {
          contexts: {
            graphql: {
              operationName: operation.operationName,
              variables: operation.variables,
            },
          },
        });
      }
    }
  }
});

/**
 * Retry Link for exponential backoff on failed requests
 * Retries network errors with increasing delays
 */
const retryLink = new RetryLink({
  delay: (count) => calculateRetryDelay(count - 1, DEFAULT_RETRY_CONFIG),
  attempts: (count, operation, error) => {
    // Don't retry if we've exceeded max retries
    if (count > DEFAULT_RETRY_CONFIG.maxRetries) {
      return false;
    }

    // Only retry network errors, not GraphQL errors
    const isNetworkError =
      error?.message?.includes("Network") ||
      error?.message?.includes("Failed to fetch") ||
      !navigator.onLine;

    if (!isNetworkError) {
      return false;
    }

    // Don't retry auth errors
    const networkErr = error as Error & { statusCode?: number };
    if (networkErr.statusCode === 401 || networkErr.statusCode === 403) {
      return false;
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        `Retrying request (attempt ${count}/${DEFAULT_RETRY_CONFIG.maxRetries})`,
        operation.operationName,
      );
    }

    return true;
  },
});

const HttpLink = new ApolloHttpLink({
  uri: `${process.env.NEXT_PUBLIC_API_URL as string}/graphql`,
});

const wsLink = () =>
  new GraphQLWsLink(
    createClient({
      url: `${process.env.NEXT_PUBLIC_WS_API_URL as string}/graphql`,
      retryAttempts: Infinity,
      shouldRetry: () => true,
    }),
  );

const SplitLink =
  typeof window !== "undefined"
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          );
        },
        wsLink(),
        HttpLink,
      )
    : HttpLink;

export const createApolloClient = () => {
  const link = ApolloLink.from([
    ErrorLink,
    AuthLink,
    retryLink,
    ...(typeof window === "undefined" ? [HttpLink] : [SplitLink]),
  ]);

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      mutate: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
  });
};
